/**
 * Utility functions for API calls
 */

/**
 * Validates an affiliate code by checking with the backend
 *
 * @param {string} code - The affiliate code to validate
 * @returns {Promise<boolean>} - Whether the code is valid
 */
export const validateAffiliateCode = async (code: string): Promise<boolean> => {
  if (!code) return false;

  try {
    const response = await fetch(`/api/affiliates/validate/${code}`);
    if (!response.ok) {
      return false;
    }

    const data = await response.json();
    return data.valid;
  } catch (error) {
    console.error('Error validating affiliate code:', error);
    return false;
  }
};

/**
 * Tracks a purchase with an affiliate code
 *
 * @param {string} orderId - The order ID from PayPal
 * @param {string|number} amount - The purchase amount
 * @param {string} affiliateCode - The affiliate code
 * @param {string} type - The purchase type ('one-time' or 'subscription')
 * @returns {Promise<object>} - The response from the server
 */
/**
 * Check if a customer already exists by email
 */
export const checkCustomerEmail = async (
  email: string
): Promise<{ exists: boolean; customer?: { id: string; name: string; email: string } }> => {
  if (!email) return { exists: false };

  try {
    const response = await fetch(`/api/customers/email/${encodeURIComponent(email)}`);
    if (!response.ok) {
      return { exists: false };
    }
    return await response.json();
  } catch (error) {
    console.error('Error checking customer email:', error);
    return { exists: false };
  }
};

/**
 * Validate a code as either an affiliate code or a coupon code
 */
export type CodeValidationResult = {
  valid: boolean;
  type?: 'affiliate' | 'coupon';
  code?: string;
  partnerName?: string;
  discountType?: 'free' | 'percent' | 'fixed' | null;
  discountValue?: number | null;
  product?: string;
  paymentRequired?: boolean;
  error?: string;
};

export const validateCode = async (
  code: string,
  product?: string
): Promise<CodeValidationResult> => {
  if (!code) return { valid: false, error: 'No code provided' };

  try {
    const response = await fetch('/api/codes/validate', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ code, product }),
    });

    return await response.json();
  } catch (error) {
    console.error('Error validating code:', error);
    return { valid: false, error: 'Failed to validate code' };
  }
};

/**
 * Redeem a coupon code
 */
export const redeemCoupon = async (
  code: string,
  customerEmail: string,
  product: string,
  referredByCode?: string,
  referredByType?: string
): Promise<{ success?: boolean; error?: string }> => {
  try {
    const response = await fetch('/api/coupons/redeem', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ code, customerEmail, product, referredByCode, referredByType }),
    });

    return await response.json();
  } catch (error) {
    console.error('Error redeeming coupon:', error);
    return { error: 'Failed to redeem coupon' };
  }
};

export const trackAffiliatePurchase = async (
  orderId: string,
  amount: string | number,
  affiliateCode: string,
  type: 'one-time' | 'subscription' = 'one-time',
  referredByCode?: string,
  referredByType?: string
): Promise<Record<string, unknown>> => {
  try {
    const response = await fetch('/api/purchase/track', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        orderId,
        amount,
        affiliateCode,
        type,
        referredByCode,
        referredByType,
      }),
    });

    return await response.json();
  } catch (error: unknown) {
    console.error('Error tracking affiliate purchase:', error);
    return { error: error instanceof Error ? error.message : 'Request failed' };
  }
};
